import { runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useRef } from 'react'
import modalService from 'src/service/modal.service'
import { CommonPrintService, worksheetPrintService } from 'src/service/print'

import _Button from '@/component/atoms/Button/_Button/_Button'
import { default as Checkbox } from '@/component/atoms/Checkbox/_Checkbox'
import { default as CheckboxButton } from '@/component/atoms/CheckboxButton'
import { Dropdown, DropdownItem } from '@/component/molecules/Dropdown'
import { default as Input } from '@/component/molecules/Input'
import { default as PlusMinusInput } from '@/component/molecules/PlusMinusInput'
import { Radio } from '@/component/molecules/Radio'
import CustomPrintModalBanner from '@/page/CustomPrint/CustomPrintModalBanner'
import { commonErrorMessage } from '@/utils/toast'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import type { WorksheetExportType } from '../../Download/type'
import S from '../Print.style'
import StudentNameBox from './StudentNameBox'

type Props = {
  worksheets: (typeof worksheetPrintService)['printWorksheetList']
  afterPrintFn: () => void
  ignoredForDeleted?: boolean
  type?: WorksheetExportType
  disabledRangeOptions?: ('OMR' | 'ANSWER' | 'QUICK_ANSWER' | 'PROBLEM')[]
  description?: string
  showAsExamSheet?: boolean
  hideStudentNameOption?: boolean
  hideBanner?: boolean
}

export const WorksheetPrintModal = observer(
  ({
    worksheets,
    afterPrintFn,
    ignoredForDeleted,
    type,
    disabledRangeOptions,
    description,
    showAsExamSheet,
    hideStudentNameOption,
    hideBanner,
  }: Props) => {
    const scrollAreaRef = useRef<HTMLDivElement>(null)
    const sheetType = (() => {
      if (showAsExamSheet || type === 'KMM' || type === 'KMM_PRACTICE_TEST') return '시험지'
      return '학습지'
    })()

    const worksheetList = worksheets?.map(({ title, id, assignedStudentWorksheetList }) => ({
      title,
      id,
      assignedStudentWorksheetList,
    }))

    const store = useLocalObservable(() => ({
      onPrintClick: async () => {
        try {
          await worksheetPrintService.onPrintWorksheet(
            worksheetPrintService.printWorksheetList,
            type,
          )
          afterPrintFn()
        } catch (error) {
          commonErrorMessage(error)
        }
      },
    }))

    useEffect(() => {
      worksheetList?.forEach(({ id, title, assignedStudentWorksheetList }) => {
        if (assignedStudentWorksheetList) {
          worksheetPrintService.printWorksheetMap.set(id, {
            title,
            assignedStudentWorksheetList: assignedStudentWorksheetList.map(
              ({ studentId, studentName, studentWorksheetId }) => ({
                studentId,
                studentName,
                studentWorksheetId,
              }),
            ),
          })
        }
      })

      worksheetPrintService.fetchInitPrintListByWorksheetList(
        worksheetPrintService.printWorksheetList,
        ignoredForDeleted,
        type,
        hideStudentNameOption,
      )

      return () => {
        worksheetPrintService.printWorksheetMap.clear()
      }
    }, [])

    if (worksheetPrintService.printPageInfoList === null) {
      return null
    }

    return (
      <div css={[S.Wrap, S.Worksheet]}>
        <header>
          <h3>{sheetType} 인쇄</h3>
          <span>선택한 학습지 총 {worksheetList?.length ?? 0}개</span>
        </header>

        {!hideBanner && (
          <CustomPrintModalBanner
            onClick={() => {
              modalService.closeModal()
              mixpanelClient.track('T62-03_커스텀프린팅_진입점_학습지인쇄배너')
            }}
          />
        )}
        <section className="print-body" ref={scrollAreaRef}>
          {!hideStudentNameOption && worksheetPrintService.uiStore.useStudentOption && (
            <StudentNameBox type={type} />
          )}

          <div className="print-row print-count">
            <p className="row-left">매수</p>
            <div className="row-right">
              <PlusMinusInput
                max={CommonPrintService.MAX_PRINT_QUANTITY}
                value={worksheetPrintService.printOption.copyNumber}
                onChange={(copyNumber) => {
                  worksheetPrintService.printOption.update({ copyNumber })
                }}
                editable
              />
            </div>
          </div>

          <div className="print-row print-range">
            <p className="row-left">범위</p>
            <div className="row-right">
              <Radio.Group
                className="range-radio"
                value={worksheetPrintService.uiStore.visibleRangeOption}
                onChange={(event) =>
                  worksheetPrintService.uiStore.update({
                    visibleRangeOption: event.target.value as 'ALL' | 'SELECT',
                  })
                }
              >
                <Radio value="ALL">
                  전체 &nbsp;
                  {worksheetPrintService.isSinglePrintTarget && (
                    <span className="all-page-range">
                      ({worksheetPrintService.maximumPageRange})
                    </span>
                  )}
                </Radio>
                <Radio value="SELECT">페이지 선택</Radio>
              </Radio.Group>
              {worksheetPrintService.uiStore.visibleRangeOption === 'SELECT' && (
                <>
                  {worksheetPrintService.isSinglePrintTarget && (
                    <>
                      <div className="range-insert-area">
                        <Input
                          type="text"
                          className={`${
                            !worksheetPrintService.rangeOption.isRangedInputText ? 'warning' : ''
                          }`}
                          placeholder="페이지를 입력해주세요."
                          value={worksheetPrintService.rangeOption.inputText}
                          onChange={(event) =>
                            worksheetPrintService.onChangePageInput(event.target.value)
                          }
                        />
                        <p>예시) 1,3,5-7</p>
                      </div>
                      {!worksheetPrintService.rangeOption.isRangedInputText && (
                        <p className="warning page-error">페이지를 범위에 맞게 입력해주세요.</p>
                      )}
                      {worksheetPrintService.rangeOption.isOverMaxPage(
                        worksheetPrintService.maxPage,
                      ) && (
                        <p className="warning page-error">
                          최대 페이지를 초과하여 인쇄할 수 없습니다.
                        </p>
                      )}
                    </>
                  )}
                  <div className="range-option-area">
                    <CheckboxButton
                      disabled={disabledRangeOptions?.includes('PROBLEM')}
                      checked={worksheetPrintService.rangeOption.problem}
                      onClick={() =>
                        worksheetPrintService.onChangeRangeOption({
                          problem: !worksheetPrintService.rangeOption.problem,
                        })
                      }
                    >
                      문제지
                    </CheckboxButton>
                    <CheckboxButton
                      disabled={disabledRangeOptions?.includes('QUICK_ANSWER')}
                      checked={worksheetPrintService.rangeOption.quickAnswer}
                      onClick={() =>
                        worksheetPrintService.onChangeRangeOption({
                          quickAnswer: !worksheetPrintService.rangeOption.quickAnswer,
                        })
                      }
                    >
                      빠른정답
                    </CheckboxButton>
                    <CheckboxButton
                      disabled={disabledRangeOptions?.includes('ANSWER')}
                      checked={worksheetPrintService.rangeOption.answer}
                      onClick={() =>
                        worksheetPrintService.onChangeRangeOption({
                          answer: !worksheetPrintService.rangeOption.answer,
                        })
                      }
                    >
                      정답해설
                    </CheckboxButton>

                    <CheckboxButton
                      disabled={disabledRangeOptions?.includes('OMR')}
                      checked={worksheetPrintService.rangeOption.omr}
                      onClick={() =>
                        worksheetPrintService.onChangeRangeOption({
                          omr: !worksheetPrintService.rangeOption.omr,
                        })
                      }
                    >
                      OMR
                    </CheckboxButton>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="print-row print-option">
            <p className="row-left">옵션</p>
            <div className="row-right ">
              <Checkbox
                onChange={(isDuplex) => worksheetPrintService.printOption.update({ isDuplex })}
                checked={worksheetPrintService.printOption.isDuplex}
                text={<span>양면</span>}
              />
              <Checkbox
                onChange={(isMonochrome) =>
                  worksheetPrintService.printOption.update({ isMonochrome })
                }
                checked={worksheetPrintService.printOption.isMonochrome}
                text={<span>흑백</span>}
              />
              <Checkbox
                onChange={(isReverse) => worksheetPrintService.printOption.update({ isReverse })}
                checked={worksheetPrintService.printOption.isReverse}
                text={<span>역순</span>}
              />
            </div>
          </div>
          {worksheetPrintService.useOrderOptionUseable && (
            <div className="print-row">
              <p className="row-left">인쇄 순서</p>
              <div className="row-right">
                <Radio.Group
                  className="range-radio"
                  value={worksheetPrintService.order}
                  onChange={(event) =>
                    runInAction(() => {
                      worksheetPrintService.order = event.target
                        .value as (typeof worksheetPrintService)['order']
                    })
                  }
                >
                  <Radio value="by-date">학습지 생성 순</Radio>
                  <Radio value="by-student">학생별</Radio>
                </Radio.Group>
              </div>
            </div>
          )}
          {worksheetPrintService.printAgent && (
            <div className="print-row printer-select">
              <p className="row-left">프린터 선택</p>
              <div className="row-right">
                <div className="print-dropdown">
                  <Dropdown
                    height={40}
                    width={344}
                    listHeight={216}
                    onOpen={() => {
                      setTimeout(() => {
                        scrollAreaRef.current?.scrollTo({
                          top: scrollAreaRef.current?.scrollHeight,
                          behavior: 'smooth',
                        })
                      }, 0)
                    }}
                    value={`${worksheetPrintService.printAgent.selectedPrinter?.machineId}:${worksheetPrintService.printAgent?.selectedPrinter?.displayName}`}
                    onChange={(value: string) => {
                      const [mid, printName] = value.split(':')
                      const printer = worksheetPrintService.printAgent?.printerList.find(
                        ({ displayName, machineId }) =>
                          displayName === printName && machineId === mid,
                      )

                      if (printer) {
                        worksheetPrintService.selectPrinter(printer)
                      }
                    }}
                  >
                    {worksheetPrintService.printAgent.printerList.map((printer) => (
                      <DropdownItem
                        key={`${printer.machineId}:${printer.displayName}`}
                        data-mathflat-unique-id={`${printer.machineId}:${printer.displayName}`}
                        value={`${printer.machineId}:${printer.displayName}`}
                      >
                        {printer.displayName}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                </div>
              </div>
            </div>
          )}
        </section>
        <footer className="print-footer">
          {description && <div className="description">{description}</div>}
          <_Button
            minWidth={144}
            size="m"
            disabled={worksheetPrintService.isPrintDisabled}
            theme="primary"
            onClick={store.onPrintClick}
          >
            인쇄하기
          </_Button>
        </footer>
      </div>
    )
  },
)
